import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Play, ArrowRight, X, Music, Gift, Cake, Headphones, ListMusic, Loader2 } from 'lucide-react';
import { apiFetch } from '../App';
import { toast } from 'sonner';
import { usePlayer } from '../context/PlayerContext';

// Image assets
const BACKGROUND_IMAGE = "/assets/migrated/background.png";
const LOGO_IMAGE = "/assets/brand/logo-inline.png";

// Step images from stock
const STEP1_IMAGE = "/assets/migrated/step-create.jpg";
const STEP2_IMAGE = "/assets/migrated/step-listen.jpg";
const STEP3_IMAGE = "/assets/migrated/step-gift.jpg";

// Animated Stars Component
const AnimatedStars = () => {
  // Generate random stars
  const stars = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: Math.random() * 3 + 1,
    duration: Math.random() * 3 + 2,
    delay: Math.random() * 5,
    driftX: (Math.random() - 0.5) * 100,
    driftY: (Math.random() - 0.5) * 50,
  }));

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {stars.map((star) => (
        <div
          key={star.id}
          className="absolute rounded-full bg-white"
          style={{
            left: `${star.left}%`,
            top: `${star.top}%`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            animation: `twinkle ${star.duration}s ease-in-out infinite, drift ${star.duration * 10}s ease-in-out infinite`,
            animationDelay: `${star.delay}s`,
            '--drift-x': `${star.driftX}px`,
            '--drift-y': `${star.driftY}px`,
          }}
        />
      ))}
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes drift {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(var(--drift-x), var(--drift-y)); }
        }
      `}</style>
    </div>
  );
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Share modal state
  const [shareModal, setShareModal] = useState(null); // { type: 'song' | 'gift', data: {...} }
  const [loadingShare, setLoadingShare] = useState(false);
  // v3.0.144 — Featured Playlists on landing
  const [featuredPlaylists, setFeaturedPlaylists] = useState([]);
  // v3.0.148 — Play All button state
  const [loadingPlayAll, setLoadingPlayAll] = useState(false);
  const { playQueue } = usePlayer();

  const handlePlayAllFeatured = async () => {
    if (featuredPlaylists.length === 0 || loadingPlayAll) return;
    setLoadingPlayAll(true);
    try {
      // Fetch each featured playlist's songs in parallel — anonymous route
      // needs the access_code in query.
      const B = process.env.REACT_APP_BACKEND_URL;
      const responses = await Promise.all(
        featuredPlaylists.map((p) =>
          fetch(`${B}/api/public/playlists/${p.playlist_id}?code=${p.access_code}`, { credentials: 'omit' })
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null),
        ),
      );
      const all = [];
      for (const r of responses) {
        const songs = Array.isArray(r?.songs) ? r.songs : [];
        for (const s of songs) {
          if (s?.audio_url && !all.some((x) => x.song_id === s.song_id)) {
            all.push(s);
          }
        }
      }
      if (all.length === 0) {
        toast.error('Featured playlists have no playable songs yet');
        return;
      }
      playQueue(all, {
        startIndex: 0,
        title: 'Featured mix',
        playlistId: null,
        autoplay: true,
      });
      toast.success(`Playing ${all.length} featured song${all.length === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error('Could not start Featured mix');
    } finally {
      setLoadingPlayAll(false);
    }
  };

  // Fetch featured public playlists (anonymous, no auth needed)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiFetch('/public/playlists/featured?limit=6');
        if (cancelled) return;
        const arr = Array.isArray(data?.playlists) ? data.playlists : [];
        setFeaturedPlaylists(arr.filter((p) => p.song_count > 0));
      } catch (e) {
        // Silent — this section is optional; just don't render
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Check for share params on load
  useEffect(() => {
    const shareType = searchParams.get('share');
    const shareId = searchParams.get('id');
    
    if (shareType && shareId) {
      loadShareData(shareType, shareId);
    }
  }, [searchParams]);
  
  const loadShareData = async (type, id) => {
    setLoadingShare(true);
    try {
      let endpoint = '';
      if (type === 'song') {
        endpoint = `/share/song/${id}/data`;
      } else if (type === 'gift') {
        // Gift endpoint is already public
        endpoint = `/gifts/${id}`;
      } else if (type === 'birthday') {
        endpoint = `/share/birthday/${id}/data`;
      }
      
      if (!endpoint) {
        setSearchParams({});
        return;
      }
      
      const { data, error } = await apiFetch(endpoint);
      if (data && !error) {
        // Normalize gift data structure
        if (type === 'gift' && data.gift) {
          setShareModal({ 
            type, 
            data: {
              ...data.gift,
              song: data.song,
              cover: data.cover
            }
          });
        } else {
          setShareModal({ type, data });
        }
      } else {
        // Clear params if not found
        setSearchParams({});
      }
    } catch (error) {
      console.error('Error loading share data:', error);
      setSearchParams({});
    } finally {
      setLoadingShare(false);
    }
  };
  
  const closeShareModal = () => {
    setShareModal(null);
    setSearchParams({});
  };

  const steps = [
    {
      number: 1,
      title: "Start with a thought",
      image: STEP1_IMAGE,
      description: [
        "Type a theme, story, or any words that come to mind.",
        "Pick a style and a mood.",
        "Select Generate Song."
      ]
    },
    {
      number: 2,
      title: "Listen and refine",
      image: STEP2_IMAGE,
      description: [
        "Our system will compose a full song",
        "for you to hear and tweak to your liking.",
        "Change lyrics, instruments, vocals, and more."
      ]
    },
    {
      number: 3,
      title: "Download and share",
      image: STEP3_IMAGE,
      description: [
        "Download your finished song as a",
        "professional-quality recording",
        "or send a free gift card to share",
        "a link to your song."
      ]
    }
  ];

  return (
    <div 
      className="min-h-screen text-white relative"
      style={{
        backgroundImage: `url('${BACKGROUND_IMAGE}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Animated Stars */}
      <AnimatedStars />
      
      {/* HERO SECTION */}
      <section className="text-center px-6 pt-16 pb-20 relative z-10">
        {/* LOGO */}
        <div className="flex justify-center mb-8">
          <img
            src={LOGO_IMAGE}
            alt="Wrapped In Music"
            className="h-11 md:h-16 w-auto"
            data-testid="hero-logo"
          />
        </div>

        {/* HEADLINE */}
        <h1 className="text-2xl md:text-4xl lg:text-5xl font-light mb-4 tracking-wide">
          Turn your ideas into songs in minutes — no experience needed.
        </h1>

        {/* SUBHEAD */}
        <p className="text-lg md:text-xl text-white/80 mb-4">
          Describe your song with as little as one word. Pick a style and mood. Get a song.
        </p>

        {/* SUPPORT LINE */}
        <p className="text-white/70 mb-10">
          No account? Try our free demo below — one full song, no card required.
        </p>

        {/* BUTTONS — 3-way split so a visitor can try before signing up */}
        <div className="flex justify-center gap-4 flex-wrap mb-6">
          <Button
            onClick={() => navigate('/demo/create')}
            className="px-8 py-6 rounded-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-medium text-lg transition-all shadow-lg shadow-purple-500/30"
            data-testid="try-demo-btn"
          >
            Try our free demo <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/auth?mode=login')}
            className="px-8 py-6 rounded-full border-2 border-white/40 bg-transparent hover:bg-white/10 text-white font-medium text-lg transition-all"
            data-testid="landing-login-btn"
          >
            Log In
          </Button>
          <Button
            onClick={() => navigate('/auth?mode=signup')}
            className="px-8 py-6 rounded-full bg-white text-slate-900 hover:bg-white/90 font-medium text-lg transition-all shadow-lg"
            data-testid="landing-signup-btn"
          >
            Sign Up
          </Button>
        </div>
      </section>

      {/* DIVIDER */}
      <div className="w-full h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>

      {/* v3.0.144 — FEATURED PLAYLISTS — anonymous discovery */}
      {featuredPlaylists.length > 0 && (
        <section
          className="px-6 pt-14 pb-4 relative z-10"
          data-testid="featured-playlists-section"
        >
          <div className={`max-w-6xl mx-auto ${featuredPlaylists.length < 6 ? 'text-center' : ''}`}>
            <div className={`flex items-end mb-2 ${featuredPlaylists.length < 6 ? 'justify-center' : 'justify-between'}`}>
              <div>
                <div className={`flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-widest mb-2 ${featuredPlaylists.length < 6 ? 'justify-center' : ''}`}>
                  <ListMusic size={14} /> Now trending
                </div>
                <h2 className="text-xl md:text-2xl font-medium text-white">
                  Playlists our members are sharing
                </h2>
                <p className="text-sm text-white/70 mt-1">
                  No sign-in needed — tap a playlist to listen instantly.
                </p>
                {/* v3.0.148 — Play All CTA */}
                <div className={`mt-3 ${featuredPlaylists.length < 6 ? 'flex justify-center' : ''}`}>
                  <button
                    onClick={handlePlayAllFeatured}
                    disabled={loadingPlayAll}
                    className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-medium text-sm px-4 py-2 rounded-full transition-all hover:scale-[1.03] disabled:opacity-70 disabled:cursor-wait"
                    data-testid="landing-play-featured-btn"
                  >
                    {loadingPlayAll ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Play size={14} fill="black" />
                    )}
                    {loadingPlayAll ? 'Loading…' : 'Play featured mix'}
                  </button>
                </div>
              </div>
            </div>
            <div
              className={
                featuredPlaylists.length < 6
                  ? 'flex flex-wrap justify-center gap-3 sm:gap-4 mt-6'
                  : 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mt-6'
              }
            >
              {featuredPlaylists.map((p) => (
                <Link
                  key={p.playlist_id}
                  to={`/p/${p.playlist_id}?code=${p.access_code}`}
                  className="group bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg overflow-hidden transition-all hover:scale-[1.02] hover:shadow-xl hover:shadow-emerald-500/10 w-40 sm:w-48"
                  data-testid={`featured-playlist-${p.playlist_id}`}
                >
                  <div className="aspect-square bg-gradient-to-br from-emerald-500/20 to-purple-500/20 relative overflow-hidden">
                    {p.cover_url ? (
                      <img
                        src={p.cover_url}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ListMusic size={40} className="text-white/40" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-colors">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-emerald-500 text-black rounded-full p-3">
                        <Play size={20} fill="black" />
                      </div>
                    </div>
                  </div>
                  <div className="p-2.5">
                    <h3 className="font-semibold text-white text-sm line-clamp-1">{p.name}</h3>
                    <div className="flex items-center gap-2 mt-1 text-[11px] text-zinc-400">
                      <span
                        className="flex items-center gap-1"
                        title="Songs in this playlist"
                      >
                        <Music size={11} /> {p.song_count} song{p.song_count === 1 ? '' : 's'}
                      </span>
                      <span aria-hidden="true" className="text-zinc-600">•</span>
                      <span
                        className="flex items-center gap-1 text-emerald-300"
                        title="Times this share link has been opened"
                      >
                        <Headphones size={11} /> {p.play_count} play{p.play_count === 1 ? '' : 's'}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LANGUAGE SUPPORT — reassures international visitors before
          they scroll into "How it works". Kept as a small, understated
          paragraph rather than a badge so it reads like an editorial
          note. */}
      <section className="px-6 pt-10 pb-2 relative z-10">
        <p
          className="max-w-3xl mx-auto text-center text-sm text-white/60 italic leading-relaxed"
          data-testid="language-support-note"
        >
          Although English is the primary and most robustly supported language,
          our songwriters can create songs in many other languages including
          English, French, German, Spanish, Italian, Arabic, Korean, Hindi,
          Japanese, Turkish and Russian — depending on which songwriter you
          choose for your song.
        </p>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section className="px-6 py-20 text-center relative z-10">
        <h2 className="text-xl md:text-3xl mb-2 font-light">
          Create and share your custom song on social media in minutes
        </h2>
        <p className="text-sm text-white/80 mb-16">More advanced features are selectable to further enhance your songs and creative abilities as you progress.</p>

        <div className="grid md:grid-cols-3 gap-8 lg:gap-12 max-w-6xl mx-auto">
          {steps.map((step) => (
            <div key={step.number} className="flex flex-col items-center">
              {/* Step Number */}
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-xl font-bold mb-4 shadow-lg shadow-purple-500/30">
                {step.number}
              </div>

              {/* Step Title */}
              <h3 className="text-lg md:text-xl font-medium mb-6 text-white">
                {step.title}
              </h3>

              {/* Step Image */}
              <div className="relative mb-6 w-full max-w-[280px] aspect-square rounded-2xl overflow-hidden shadow-2xl shadow-purple-500/20">
                <img
                  src={step.image}
                  alt={step.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>

              {/* Step Description */}
              <div className="text-white/70 text-sm leading-relaxed">
                {step.description.map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SHARE MODAL OVERLAY */}
      {(shareModal || loadingShare) && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={closeShareModal}
        >
          <div 
            className="relative bg-gradient-to-br from-purple-900/90 to-slate-900/90 border border-purple-500/30 rounded-2xl max-w-lg w-full p-6 shadow-2xl shadow-purple-500/20"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={closeShareModal}
              className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
              data-testid="share-modal-close"
            >
              <X className="h-6 w-6" />
            </button>

            {loadingShare ? (
              <div className="text-center py-12">
                <div className="inline-block h-8 w-8 border-2 border-white/30 border-t-purple-500 rounded-full animate-spin mb-4"></div>
                <p className="text-white/70">Loading...</p>
              </div>
            ) : shareModal?.type === 'song' ? (
              /* SONG SHARE MODAL */
              <div className="text-center">
                {/* Song artwork */}
                <div className="relative w-48 h-48 mx-auto mb-6 rounded-xl overflow-hidden shadow-lg shadow-purple-500/30">
                  {shareModal.data.image_url ? (
                    <img 
                      src={shareModal.data.image_url} 
                      alt={shareModal.data.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-600 to-purple-900 flex items-center justify-center">
                      <Music className="h-16 w-16 text-white/50" />
                    </div>
                  )}
                </div>

                <h2 className="text-2xl font-bold text-white mb-2">
                  🎵 {shareModal.data.title || 'Untitled Song'}
                </h2>
                
                {shareModal.data.tags && (
                  <p className="text-white/70 text-sm mb-4 line-clamp-2">
                    {shareModal.data.tags}
                  </p>
                )}

                {/* Audio preview */}
                {shareModal.data.audio_url && (
                  <div className="mb-6">
                    <audio 
                      controls 
                      className="w-full rounded-lg"
                      data-testid="share-modal-audio"
                    >
                      <source src={shareModal.data.audio_url} type="audio/mpeg" />
                    </audio>
                  </div>
                )}

                <p className="text-white/60 text-sm mb-6">
                  Created with Wrapped In Music - AI-powered songwriting
                </p>

                <Button
                  onClick={() => navigate('/auth')}
                  className="w-full py-4 rounded-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-medium text-lg transition-all shadow-lg shadow-purple-500/30"
                  data-testid="share-modal-signup-btn"
                >
                  Create Your Own Song <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            ) : shareModal?.type === 'gift' ? (
              /* GIFT CARD SHARE MODAL */
              <div className="text-center">
                {/* Gift icon badge */}
                <div className="flex justify-center mb-4">
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-3 rounded-full shadow-lg shadow-amber-500/30">
                    <Gift className="h-8 w-8 text-white" />
                  </div>
                </div>

                {/* Cover/Song artwork */}
                <div className="relative w-48 h-48 mx-auto mb-4 rounded-xl overflow-hidden shadow-lg shadow-purple-500/30">
                  {shareModal.data.cover?.image_url ? (
                    <img 
                      src={shareModal.data.cover.image_url} 
                      alt="Gift cover"
                      className="w-full h-full object-cover"
                    />
                  ) : shareModal.data.song?.image_url ? (
                    <img 
                      src={shareModal.data.song.image_url} 
                      alt={shareModal.data.song.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-amber-600 to-purple-900 flex items-center justify-center">
                      <Gift className="h-16 w-16 text-white/50" />
                    </div>
                  )}
                </div>

                <h2 className="text-xl font-bold text-white mb-1">
                  🎁 A Musical Gift for {shareModal.data.recipient_name || 'You'}
                </h2>
                
                <h3 className="text-lg text-purple-300 mb-3">
                  🎵 {shareModal.data.song?.title || 'A Special Song'}
                </h3>
                
                {shareModal.data.message && (
                  <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
                    <p className="text-white/80 text-sm italic">
                      "{shareModal.data.message}"
                    </p>
                    {shareModal.data.sender_name && (
                      <p className="text-white/50 text-xs mt-2">
                        — {shareModal.data.sender_name}
                      </p>
                    )}
                  </div>
                )}

                {/* Audio preview */}
                {shareModal.data.song?.audio_url && (
                  <div className="mb-6">
                    <audio 
                      controls 
                      className="w-full rounded-lg"
                      data-testid="share-modal-gift-audio"
                    >
                      <source src={shareModal.data.song.audio_url} type="audio/mpeg" />
                    </audio>
                  </div>
                )}

                <p className="text-white/60 text-sm mb-6">
                  Send personalized song gifts to your loved ones
                </p>

                <Button
                  onClick={() => navigate('/auth')}
                  className="w-full py-4 rounded-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-medium text-lg transition-all shadow-lg shadow-purple-500/30"
                  data-testid="share-modal-gift-signup-btn"
                >
                  Create Your Own Gift <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            ) : shareModal?.type === 'birthday' ? (
              /* BIRTHDAY VIDEO SHARE MODAL */
              <div className="text-center">
                {/* Birthday icon badge */}
                <div className="flex justify-center mb-4">
                  <div className="bg-gradient-to-br from-pink-500 to-rose-600 p-3 rounded-full shadow-lg shadow-pink-500/30">
                    <Cake className="h-8 w-8 text-white" />
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-white mb-2">
                  🎂 Happy Birthday {shareModal.data.name || 'Someone Special'}!
                </h2>
                
                <p className="text-white/70 text-sm mb-4">
                  A personalized birthday song video created with AI
                </p>

                {/* Video preview */}
                {shareModal.data.video_url && (
                  <div className="mb-6 rounded-xl overflow-hidden shadow-lg">
                    <video 
                      controls 
                      className="w-full max-h-64"
                      poster={LOGO_IMAGE}
                      data-testid="share-modal-birthday-video"
                    >
                      <source src={shareModal.data.video_url} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                )}

                <p className="text-white/60 text-sm mb-6">
                  Create personalized birthday songs for your loved ones!
                </p>

                <Button
                  onClick={() => navigate('/auth')}
                  className="w-full py-4 rounded-full bg-gradient-to-r from-pink-600 to-rose-500 hover:from-pink-500 hover:to-rose-400 text-white font-medium text-lg transition-all shadow-lg shadow-pink-500/30"
                  data-testid="share-modal-birthday-signup-btn"
                >
                  Create a Birthday Song <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
